import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { collection, query, where, doc, getDoc, updateDoc, onSnapshot, addDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuthContext } from '../contexts/AuthContext';

export default function useSpectatorGame(gameCode) {
  const { user, linkedProfile } = useAuthContext();
  const [players, setPlayers] = useState([]);
  const [currentPlayerIndex, setCurrentPlayerIndex] = useState(0);
  const [turnNumber, setTurnNumber] = useState(1);
  const [gamePhotoUrl, setGamePhotoUrl] = useState(null);
  const [status, setStatus] = useState(null);
  const [winner, setWinner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [gameId, setGameId] = useState(null);
  const [redemption, setRedemption] = useState(null);

  const unsubRef = useRef(null);

  const detach = useCallback(() => {
    if (unsubRef.current) {
      unsubRef.current();
      unsubRef.current = null;
    }
  }, []);

  // Look up game code → gameId, then subscribe
  useEffect(() => {
    if (!gameCode) return;

    let cancelled = false;

    async function lookupAndSubscribe() {
      setLoading(true);
      setError(null);

      try {
        const codeDoc = await getDoc(doc(db, 'gameCodes', gameCode.toUpperCase()));
        if (!codeDoc.exists()) {
          if (!cancelled) {
            setError('Game not found. Check your code and try again.');
            setLoading(false);
          }
          return;
        }

        const { gameId: id } = codeDoc.data();
        if (!cancelled) setGameId(id);

        detach();
        unsubRef.current = onSnapshot(doc(db, 'games', id), (snapshot) => {
          if (cancelled) return;
          if (!snapshot.exists()) {
            setError('Game no longer exists.');
            setLoading(false);
            return;
          }

          const data = snapshot.data();
          setPlayers(data.players || []);
          setCurrentPlayerIndex(data.currentPlayerIndex || 0);
          setTurnNumber(data.turnNumber || 1);
          setGamePhotoUrl(data.gamePhotoUrl || null);
          setStatus(data.status);
          setWinner(data.winner || null);
          setRedemption(data.redemption || null);
          setLoading(false);
        });
      } catch (e) {
        console.error('Error joining game:', e);
        if (!cancelled) {
          setError('Could not join game. Check your connection.');
          setLoading(false);
        }
      }
    }

    lookupAndSubscribe();

    return () => {
      cancelled = true;
      detach();
    };
  }, [gameCode, detach]);

  // Visibility change optimization
  useEffect(() => {
    function handleVisibility() {
      if (document.hidden && unsubRef.current) {
        detach();
      } else if (!document.hidden && gameId && !unsubRef.current) {
        unsubRef.current = onSnapshot(doc(db, 'games', gameId), (snapshot) => {
          if (snapshot.exists()) {
            const data = snapshot.data();
            setPlayers(data.players || []);
            setCurrentPlayerIndex(data.currentPlayerIndex || 0);
            setTurnNumber(data.turnNumber || 1);
            setGamePhotoUrl(data.gamePhotoUrl || null);
            setStatus(data.status);
            setWinner(data.winner || null);
            setRedemption(data.redemption || null);
          }
        });
      }
    }
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [gameId, detach]);

  // Cleanup
  useEffect(() => {
    return () => detach();
  }, [detach]);

  // ===== JOIN STATE LOGIC =====
  // Match the current user against existing player slots by Firebase auth uid.
  // A slot's authUid is set when the host added them as a Profile player whose
  // saved profile is linked to a real account.
  const matchedSlot = useMemo(() => {
    if (!user || !players.length) return null;
    return players.find(p => p.authUid && p.authUid === user.uid) || null;
  }, [user, players]);

  // True if user already has a slot (either preset and claimed, or post-approval)
  const isAlreadyPlayer = useMemo(() => {
    if (!user) return false;
    return players.some(p => p.authUid === user.uid);
  }, [user, players]);

  // Watch the current user's most recent join request for this game.
  const [myJoinRequest, setMyJoinRequest] = useState(null);
  useEffect(() => {
    if (!gameId || !user) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setMyJoinRequest(null);
      return;
    }
    const q = query(
      collection(db, 'joinRequests'),
      where('gameId', '==', gameId),
      where('fromAuthUid', '==', user.uid),
    );
    const unsub = onSnapshot(q, (snap) => {
      const reqs = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => {
          const ta = a.createdAt?.toMillis ? a.createdAt.toMillis() : (a.createdAt || 0);
          const tb = b.createdAt?.toMillis ? b.createdAt.toMillis() : (b.createdAt || 0);
          return tb - ta;
        });
      setMyJoinRequest(reqs[0] || null);
    });
    return unsub;
  }, [gameId, user]);

  // Derived state used by the spectator UI to pick which button/state to show.
  // Order matters: 'joined' beats 'matched', etc.
  const joinState = useMemo(() => {
    if (status === 'finished') return 'finished';
    if (isAlreadyPlayer) return 'joined';
    if (matchedSlot) return 'matched';
    if (myJoinRequest?.status === 'pending') return 'pending';
    if (myJoinRequest?.status === 'declined') return 'declined';
    return 'open';
  }, [status, isAlreadyPlayer, matchedSlot, myJoinRequest]);

  // Claim a preset slot: just touch the slot's lastTurn so the host can see
  // the real player has connected. No new slot is appended.
  const claimSlot = useCallback(async () => {
    if (!gameId || !user || !matchedSlot) return false;
    try {
      const updated = players.map(p =>
        p.authUid === user.uid ? { ...p, lastTurn: 'Joined!' } : p
      );
      await updateDoc(doc(db, 'games', gameId), { players: updated });
      return true;
    } catch (e) {
      console.error('Could not claim slot:', e);
      return false;
    }
  }, [gameId, user, matchedSlot, players]);

  // Ask the host to be added as a player. Creates a joinRequest doc the host
  // sees as a banner; on accept, host appends a new player slot.
  const requestToJoin = useCallback(async (displayName) => {
    if (!gameId || !user) return false;
    const name = (displayName || linkedProfile?.name || user.displayName || 'Guest')
      .toString()
      .trim();
    if (!name) return false;
    try {
      await addDoc(collection(db, 'joinRequests'), {
        gameId,
        gameCode: (gameCode || '').toUpperCase(),
        fromAuthUid: user.uid,
        fromUserId: linkedProfile?.id || null,
        fromName: name.slice(0, 80),
        status: 'pending',
        createdAt: new Date(),
      });
      return true;
    } catch (e) {
      console.error('Could not request to join:', e);
      return false;
    }
  }, [gameId, gameCode, user, linkedProfile]);

  // Backwards-compat alias used by older callers; routes to the new logic.
  const joinGame = useCallback(async () => {
    if (matchedSlot) return claimSlot();
    return requestToJoin();
  }, [matchedSlot, claimSlot, requestToJoin]);

  return {
    players, currentPlayerIndex, turnNumber, gamePhotoUrl, status, winner,
    loading, error, gameId, redemption,
    joinState, matchedSlot, myJoinRequest,
    claimSlot, requestToJoin, joinGame,
  };
}
